import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Clock3 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Reveal } from "./Reveal";
import Seo from "./Seo";
import { BLOG_ARTICLES } from "@/data/blog";

export default function BlogArticle() {
  const { slug } = useParams();
  const article = BLOG_ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#080C14]" data-testid="blog-not-found">
        <div className="text-center">
          <p className="font-display text-2xl font-bold text-slate-50">Article introuvable</p>
          <Link to="/blog" className="mt-4 inline-block text-sm font-semibold text-cyan-300 hover:text-cyan-200">
            ← Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C14]" data-testid={`blog-article-${article.slug}`}>
      <Seo
        title={`${article.title} — Blog KLMProd`}
        description={article.meta}
        path={`/blog/${article.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.meta,
          datePublished: article.date,
          author: { "@type": "Organization", name: "KLMProd", url: "https://klmprod.com" },
          image: article.img,
        }}
      />

      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            data-testid="article-back-link"
            className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au blog
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={article.img} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#080C14]/60 via-[#080C14]/80 to-[#080C14]" />
        </div>
        <div className="relative mx-auto max-w-3xl px-4 py-20 sm:px-6 sm:py-24">
          <Reveal>
            <p className="flex items-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1.5">
                <CalendarDays size={12} className="text-cyan-400" />
                {format(parseISO(article.date), "d MMMM yyyy", { locale: fr })}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock3 size={12} className="text-cyan-400" />
                {article.minutes} min de lecture
              </span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display mt-5 text-3xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-4xl lg:text-5xl" data-testid="article-title">
              {article.title}
            </h1>
          </Reveal>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
        {article.sections.map((s, i) => (
          <Reveal key={s.h2} delay={i * 0.05}>
            <section className="mt-10" data-testid={`article-section-${i + 1}`}>
              <h2 className="font-display text-2xl font-bold text-slate-100">{s.h2}</h2>
              {s.paragraphs.map((p, j) => (
                <p key={j} className="mt-4 text-base leading-relaxed text-slate-400">
                  {p}
                </p>
              ))}
            </section>
          </Reveal>
        ))}

        <Reveal delay={0.15}>
          <div className="mt-14 rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-[#0E1524] via-[#0B1E33] to-[#0E1524] p-8 text-center sm:p-10">
            <h2 className="font-display text-xl font-extrabold text-slate-50 sm:text-2xl">
              Un événement en vue ? <span className="text-gradient">Parlons-en.</span>
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-slate-400">
              Devis gratuit et sans engagement, réponse sous 48 h.
            </p>
            <Link
              to="/#devis"
              data-testid="article-cta-devis"
              className="bg-brand-gradient group mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-[#04121f] transition-transform duration-300 hover:scale-105"
            >
              Demander un devis
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </article>
    </div>
  );
}
